import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ReportPageResolverData } from '../models/IReportPageData';
import { OptionsService } from '../services/options.service';
import { ReportDetailService } from '../services/report-details.service';
import { decodeReportRoutePayload, ShareTokenService } from '../services/share-token.service';
import { selectUser } from '../store/selectors/userData.selector';
import { IAppState } from '../store/state/app.state';

@Injectable()
export class ReportPageResolver implements Resolve<any> {
  constructor(
    private store: Store<IAppState>,
    private reportDetailService: ReportDetailService,
    private shareTokenService: ShareTokenService,
    private options: OptionsService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    let payload: ReturnType<typeof decodeReportRoutePayload>;
    try {
      payload = decodeReportRoutePayload(route.params.id);
    } catch {
      this.handleInvalidRoute();
      return EMPTY;
    }

    if (!payload.reportId || !payload.oktaId) {
      this.handleInvalidRoute();
      return EMPTY;
    }

    return this.store.select(selectUser).pipe(
      take(1),
      switchMap(user =>
        forkJoin([
          forkJoin({
            reportDetail: this.reportDetailService.getReportDetails(payload.reportId, payload.oktaId),
            shareToken: this.shareTokenService.createToken(payload.reportId, payload.oktaId)
          }).pipe(
            map(({ reportDetail, shareToken }): ReportPageResolverData => ({
              oktaId: user.oktaId,
              reportId: payload.reportId,
              reportDetail,
              shareToken
            }))
          ),
          of(this.options.getImagePathForReport())
        ])
      )
    );
  }

  private handleInvalidRoute(): void {
    this.snackBar.open('The report link was invalid', '', {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
    this.router.navigate(['/claims']);
  }
}
